import { IAssignmentTypeFilterOption } from '../../types';

export type CheckboxState = 'checked' | 'indeterminate' | 'unchecked';

export const getAllLeafValues = (options: IAssignmentTypeFilterOption[]): string[] => (
  options.flatMap(option => (option.children ? getAllLeafValues(option.children) : [option.value]))
);

export const getSelectedLeafOptions = (
  options: IAssignmentTypeFilterOption[],
  selected: string[],
): IAssignmentTypeFilterOption[] => (
  options.flatMap((option) => {
    if (option.children) return getSelectedLeafOptions(option.children, selected);
    return selected.includes(option.value) ? [option] : [];
  })
);

export const getCheckboxState = (option: IAssignmentTypeFilterOption, selected: string[]): CheckboxState => {
  if (!option.children) return selected.includes(option.value) ? 'checked' : 'unchecked';

  const childValues = getAllLeafValues(option.children);
  const selectedCount = childValues.filter(value => selected.includes(value)).length;

  if (selectedCount === 0) return 'unchecked';
  if (selectedCount === childValues.length) return 'checked';
  return 'indeterminate';
};

export const getSelectedChildCount = (option: IAssignmentTypeFilterOption, selected: string[]): number => (
  option.children ? getAllLeafValues(option.children).filter(value => selected.includes(value)).length : 0
);

// Toggling a leaf flips just its own value; toggling a parent group flips all of its
// children together (checked -> none selected, unchecked/indeterminate -> all selected).
export const toggleOption = (
  option: IAssignmentTypeFilterOption,
  selected: string[],
): string[] => {
  if (!option.children) {
    return selected.includes(option.value)
      ? selected.filter(value => value !== option.value)
      : [...selected, option.value];
  }

  const childValues = getAllLeafValues(option.children);
  const allSelected = childValues.every(value => selected.includes(value));
  const withoutChildren = selected.filter(value => !childValues.includes(value));

  return allSelected ? withoutChildren : [...withoutChildren, ...childValues];
};
